// Canonical reconciliation for media inference runs.
//
// `inference_update` WebSocket events are the fast path, but they are best
// effort: one can be missed while the tab is backgrounded, dropped by a proxy,
// or broadcast before the upload response has even been applied. Every
// inference-enabled run therefore also polls the canonical activity until the
// exact run it started is visible in the stored result.
//
// Run authority is activity-scoped and shared by the upload, rerun and modal
// paths through a single registry ref, so a newer action always invalidates an
// older poller instead of letting a late read overwrite fresher results.

export const CANONICAL_POLL_INTERVAL_MS = 2000;
export const CANONICAL_RUN_TIMEOUT_MS = 5 * 60 * 1000;
const CANONICAL_READ_TIMEOUT_MS = 8000;
const CANONICAL_READ_DEADLINE_MS = 9000;

export const withRequestDeadline = (promise, timeoutMs, message) => {
  let timer;
  const deadline = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new Error(message)), timeoutMs);
  });
  return Promise.race([promise, deadline]).finally(() => clearTimeout(timer));
};

// Reserve authority for an action whose run id is not known yet (the rerun
// trigger is a round trip). The reservation already outranks the previous
// owner so an in-flight poller defers instead of applying a stale read.
export const beginCanonicalRun = (registryRef, activityId) => {
  const key = String(activityId);
  const previous = registryRef.current[key] || null;
  const pending = {
    generation: (previous?.generation || 0) + 1,
    runId: null,
    previous,
    abandoned: false,
  };
  registryRef.current[key] = pending;
  return { key, previous, pending };
};

export const confirmCanonicalRun = (registryRef, key, pending, runId) => {
  if (!runId || pending.abandoned) return null;
  // A newer request may be pending while this response is in flight. Record
  // the resolved run on the original reservation and let its poller defer. If
  // the newer request fails, rollback restores this now-confirmed owner.
  pending.runId = runId;
  return pending;
};

export const abandonCanonicalRun = (registryRef, key, pending, previous) => {
  pending.abandoned = true;
  if (registryRef.current[key] !== pending) return;
  let fallback = previous;
  while (fallback?.abandoned) fallback = fallback.previous || null;
  if (fallback) registryRef.current[key] = fallback;
  else delete registryRef.current[key];
};

export const releaseCanonicalRun = (registryRef, key, owner) => {
  // Retire the owner even when a newer pending reservation currently occupies
  // the registry. If that newer request later fails, rollback must not restore
  // an owner whose poll has already terminated.
  owner.abandoned = true;
  if (registryRef.current[key] !== owner) return false;
  delete registryRef.current[key];
  return true;
};

// Uploads learn their run id from the upload response itself, so they can take
// authority in one synchronous step.
export const claimCanonicalRun = (registryRef, activityId, runId) => {
  const { key, previous, pending } = beginCanonicalRun(registryRef, activityId);
  const owner = confirmCanonicalRun(registryRef, key, pending, runId);
  if (!owner) abandonCanonicalRun(registryRef, key, pending, previous);
  return { key, owner };
};

// 'owned'      - this run still speaks for the activity
// 'superseded' - a newer run owns the activity; stop quietly
// 'deferred'   - a newer run is starting but has no id yet; keep polling
export const canonicalRunOwnership = (registryRef, key, owner) => {
  const current = registryRef.current[key];
  if (current === owner) return 'owned';
  if (!current || owner.abandoned) return 'superseded';
  return current?.runId ? 'superseded' : 'deferred';
};

/**
 * Polls the canonical activity until every image belonging to `owner.runId`
 * has a stored result for that run, then applies it.
 *
 * @param {string[]|null} fileIds Restricts the completion check to the files
 *   this run covers. Uploads only infer the newly added batch, so the
 *   activity's older images must not gate (or race) the new run.
 * @param {() => boolean} isActive Lets a caller stop polling when nothing is
 *   left to update (e.g. the activity view navigated away).
 */
export async function waitForCanonicalRun({
  activityId,
  owner,
  registryRef,
  backendService,
  applyActivity,
  fileIds = null,
  isActive = () => true,
  pollIntervalMs = CANONICAL_POLL_INTERVAL_MS,
  timeoutMs = CANONICAL_RUN_TIMEOUT_MS,
}) {
  const key = String(activityId);
  const runFileIds = fileIds ? new Set(fileIds.map(String)) : null;
  const applyIfOwned = (activity) => {
    const ownership = canonicalRunOwnership(registryRef, key, owner);
    if (ownership !== 'owned') return ownership;
    applyActivity(activity);
    return 'applied';
  };

  let lastReadError = null;
  const reconciliationDeadline = Date.now() + timeoutMs;
  while (Date.now() < reconciliationDeadline) {
    await new Promise(resolve => setTimeout(resolve, pollIntervalMs));
    if (!isActive()) return;
    if (canonicalRunOwnership(registryRef, key, owner) === 'superseded') return;

    let refreshedActivity;
    try {
      refreshedActivity = await withRequestDeadline(
        backendService.fetchActivity(activityId, { timeout: CANONICAL_READ_TIMEOUT_MS }),
        CANONICAL_READ_DEADLINE_MS,
        'The activity refresh request timed out.',
      );
      lastReadError = null;
    } catch (error) {
      lastReadError = error;
      continue;
    }

    const runImages = (refreshedActivity?.data?.files || []).filter(file => (
      file?.type?.startsWith('image')
      && (!runFileIds || runFileIds.has(String(file._id)))
    ));
    if (runImages.some(file => file.inferenceStatus === 'failed')) {
      const application = applyIfOwned(refreshedActivity);
      if (application === 'deferred') continue;
      if (application === 'superseded') return;
      throw new Error('Analysis failed. Please try again.');
    }
    const allImagesCompletedForRun = runImages.length > 0 && runImages.every(file => {
      const latestResult = file.inferences?.[file.inferences.length - 1]?.result;
      return file.inferenceStatus === 'completed' && latestResult?._session?.run_id === owner.runId;
    });
    if (allImagesCompletedForRun) {
      const application = applyIfOwned(refreshedActivity);
      if (application === 'deferred') continue;
      return;
    }
  }

  if (lastReadError) {
    throw new Error('Could not refresh the analysis. Check your connection and try again.');
  }
  throw new Error('Analysis did not finish in time. Please try again.');
}
